import { createContext, Fragment, useContext, type ReactNode } from 'react';
import type {
  EvidenceOutcome,
  FactEvidenceRef,
  JobStep,
  LifecycleState,
  PolicyFindingSeverity,
  PreviewChannel,
  RiskClass,
} from '../types';

/** Trust states an evidence item can carry (mirrors `TrustState`). */
export type EvidenceTrustState = NonNullable<FactEvidenceRef['trust_state']>;

/**
 * Every word the shared component library renders on its own.
 *
 * `packages/ui` holds no catalogue: the consuming app supplies these through
 * `UiMessagesProvider`, built from its own ICU catalogues (CLAUDE.md §6). The
 * defaults below are English and exist so a component rendered with no
 * provider — in this package's own tests, or a story — still says something
 * true rather than a raw key.
 *
 * Invariant: no message here is assembled from translated fragments by the
 * component. Anything with an argument is a function, so the app can route it
 * through one ICU message with plurals and selects intact.
 */
export interface UiMessages {
  readonly lifecycleState: (state: LifecycleState) => string;
  readonly riskClass: (risk: RiskClass) => string;
  readonly findingSeverity: (severity: PolicyFindingSeverity) => string;
  readonly jobStepStatus: (status: JobStep['status']) => string;
  /**
   * "Draft copy (Running)" — one message so word order can change. Receives
   * nodes so the step label stays its own element.
   */
  readonly jobStepWithStatus: (parts: {
    readonly label: ReactNode;
    readonly status: ReactNode;
  }) => ReactNode;
  readonly jobProgress: string;
  readonly channelName: (channel: PreviewChannel) => string;
  readonly channelPreview: (channel: PreviewChannel) => string;
  readonly close: string;
  readonly cancel: string;
  readonly confirm: string;
  readonly working: string;
  readonly brandSwitcher: {
    readonly switchBrand: string;
    readonly selectBrand: string;
    readonly noBrands: string;
  };
  readonly approvalDiff: {
    readonly contentHash: string;
    readonly destinationHash: string;
    readonly noDestination: string;
    readonly newVersion: (label: string) => string;
    readonly diff: (before: string, after: string) => string;
  };
  readonly claims: {
    readonly summary: (unsupportedCount: number) => string;
    readonly unsupported: string;
    readonly partial: (factCount: number) => string;
    readonly supported: (factCount: number) => string;
  };
  readonly cost: {
    readonly currency: string;
    readonly totalCap: string;
    readonly dailyCap: string;
    readonly committedSpend: string;
    readonly remaining: string;
    readonly activeDates: string;
    readonly targeting: string;
    readonly dateRange: (start: string, end: string) => string;
    readonly money: (amount: number, currency: string) => string;
  };
  readonly dangerous: {
    readonly title: (action: string) => string;
    /** Receives nodes (already emphasised) so the sentence keeps its markup. */
    readonly summary: (parts: {
      readonly action: ReactNode;
      readonly account: ReactNode;
      readonly brand: ReactNode;
    }) => ReactNode;
    readonly scope: string;
    readonly irreversible: string;
  };
  readonly evidence: {
    readonly title: string;
    readonly description: string;
    readonly empty: string;
    /** Shown while the evidence is still being read — never the empty message. */
    readonly loading: string;
    /** The read failed transiently; a retry may succeed. */
    readonly errorRetriable: string;
    /** The read failed and retrying will not help (e.g. forbidden, not found). */
    readonly errorTerminal: string;
    /** Label of the retry button offered for a retriable failure. */
    readonly retry: string;
    readonly source: string;
    readonly trust: (state: EvidenceTrustState) => string;
    /** Label for the exact place in the source the excerpt was found. */
    readonly locator: string;
    /** Label for when the source was retrieved/imported. */
    readonly retrievedAt: string;
    /** The claim's grounding verdict, as words (never colour alone). */
    readonly outcome: (outcome: EvidenceOutcome) => string;
    /**
     * Formats an ISO timestamp for display. The app supplies one bound to the
     * UI locale; a caller that knows the workspace timezone overrides it via
     * the drawer's `formatTimestamp` prop. Never returns the raw ISO string
     * for a valid date.
     */
    readonly formatTimestamp: (iso: string) => string;
  };
  readonly metric: {
    readonly explain: (metricName: string) => string;
    readonly definition: (metricName: string) => string;
  };
  readonly pause: {
    /** Receives the workspace already emphasised. */
    readonly headline: (workspace: ReactNode) => ReactNode;
    readonly meta: (details: {
      readonly pausedBy: string | null;
      readonly pausedAt: string | null;
      readonly reason: string | null;
    }) => string;
  };
  readonly findings: {
    readonly narrowBanner: (count: number) => string;
    readonly none: string;
    readonly suggestedFix: (fix: string) => string;
    readonly notWaivable: string;
    readonly requestWaiver: string;
  };
}

const LIFECYCLE_EN: Record<LifecycleState, string> = {
  draft: 'Draft',
  needs_review: 'Needs review',
  approved: 'Approved',
  scheduled: 'Scheduled',
  publishing: 'Publishing',
  published: 'Published',
  failed: 'Failed',
  unknown: 'Unknown — do not retry',
  cancelled: 'Cancelled',
  archived: 'Archived',
  blocked: 'Blocked',
  planning: 'Planning',
  active: 'Active',
  paused: 'Paused',
  completed: 'Completed',
  pending: 'Pending',
  rejected: 'Rejected',
  changes_requested: 'Changes requested',
  degraded: 'Degraded',
  reauth_required: 'Reauthorization required',
  revoked: 'Revoked',
  queued: 'Queued',
  leased: 'Running',
  retry_wait: 'Retrying',
  succeeded: 'Succeeded',
  dead_letter: 'Dead-lettered',
};

const RISK_EN: Record<RiskClass, string> = {
  low: 'Low risk',
  medium: 'Medium risk',
  high: 'High risk',
  critical: 'Critical risk',
};

const SEVERITY_EN: Record<PolicyFindingSeverity, string> = {
  block: 'Blocking',
  warn: 'Warning',
  info: 'Info',
};

const STEP_EN: Record<JobStep['status'], string> = {
  pending: 'Pending',
  running: 'Running',
  succeeded: 'Succeeded',
  failed: 'Failed',
  skipped: 'Skipped',
};

const CHANNEL_EN: Record<PreviewChannel, string> = {
  website: 'Website',
  blog: 'Blog',
  organic_social: 'Organic social',
  paid_social: 'Paid social',
  search_ads: 'Search ad',
  email: 'Email',
  community: 'Community post',
  app_store: 'App store listing',
  referral: 'Referral',
  direct: 'Direct message',
};

const OUTCOME_EN: Record<EvidenceOutcome, string> = {
  supported: 'Supported',
  partially_supported: 'Partially supported',
  unsupported: 'Unsupported',
  contradicted: 'Contradicted by evidence',
  insufficient_evidence: 'Insufficient evidence',
};

/** Locale-aware date and time; falls back to the input only when it is not a date. */
export function defaultFormatTimestamp(iso: string, locale = 'en-CA', timeZone?: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return new Intl.DateTimeFormat(locale, {
    dateStyle: 'medium',
    timeStyle: 'short',
    ...(timeZone ? { timeZone } : {}),
  }).format(date);
}

const TRUST_EN: Record<EvidenceTrustState, string> = {
  untrusted: 'Untrusted',
  owner_declared: 'Owner declared',
  ownership_verified: 'Ownership verified',
  quarantined: 'Quarantined',
};

/** The English defaults. The app replaces every one of them per locale. */
export const defaultUiMessages: UiMessages = {
  lifecycleState: (state) => LIFECYCLE_EN[state],
  riskClass: (risk) => RISK_EN[risk],
  findingSeverity: (severity) => SEVERITY_EN[severity],
  jobStepStatus: (status) => STEP_EN[status],
  jobStepWithStatus: ({ label, status }) => (
    <>
      {label} ({status})
    </>
  ),
  jobProgress: 'Job progress',
  channelName: (channel) => CHANNEL_EN[channel],
  channelPreview: (channel) => `${CHANNEL_EN[channel]} preview`,
  close: 'Close',
  cancel: 'Cancel',
  confirm: 'Confirm',
  working: 'Working…',
  brandSwitcher: {
    switchBrand: 'Switch brand',
    selectBrand: 'Select a brand',
    noBrands: 'No brands yet',
  },
  approvalDiff: {
    contentHash: 'Content hash',
    destinationHash: 'Destination hash',
    noDestination: 'No external destination',
    newVersion: (label) => `${label} (new version)`,
    diff: (before, after) => `Diff from ${before} to ${after}`,
  },
  claims: {
    summary: (n) =>
      n === 0
        ? 'All claims in this content are supported by verified facts.'
        : `${n} unsupported claim${n === 1 ? '' : 's'} found in this content.`,
    unsupported: 'Unsupported claim: no verified fact backs this statement.',
    partial: (n) => `Partially supported claim, linked to ${n} fact(s).`,
    supported: (n) => `Supported claim, linked to ${n} fact(s).`,
  },
  cost: {
    currency: 'Currency',
    totalCap: 'Total cap',
    dailyCap: 'Daily cap',
    committedSpend: 'Committed spend',
    remaining: 'Remaining',
    activeDates: 'Active dates',
    targeting: 'Targeting',
    dateRange: (start, end) => `${start} – ${end}`,
    money: (amount, currency) =>
      new Intl.NumberFormat('en-CA', { style: 'currency', currency }).format(amount),
  },
  dangerous: {
    title: (action) => `Confirm ${action}`,
    summary: ({ action, account, brand }) => (
      <>
        You are about to {action} as {account} for brand {brand}.
      </>
    ),
    scope: 'Scope:',
    irreversible: 'This cannot be undone:',
  },
  evidence: {
    title: 'Evidence',
    description: 'Facts and source evidence supporting the current claim.',
    empty: 'No evidence attached yet.',
    loading: 'Loading evidence…',
    errorRetriable: 'The evidence could not be loaded. This is usually temporary; try again.',
    errorTerminal:
      'The evidence cannot be loaded, and trying again will not help. Do not treat this claim as supported until it can be read.',
    retry: 'Try again',
    source: 'Source',
    trust: (state) => `Trust: ${TRUST_EN[state]}`,
    locator: 'Found at',
    retrievedAt: 'Imported',
    outcome: (outcome) => `Claim: ${OUTCOME_EN[outcome]}`,
    formatTimestamp: (iso) => defaultFormatTimestamp(iso),
  },
  metric: {
    explain: (name) => `What does ${name} mean?`,
    definition: (name) => `${name} definition`,
  },
  pause: {
    headline: (workspace) => (
      <>
        External actions are paused for {workspace}. No publish, schedule, spend, or connector-sync
        action will run until an admin resumes the workspace.
      </>
    ),
    meta: ({ pausedBy, pausedAt, reason }) =>
      [
        pausedBy ? `Paused by ${pausedBy}` : 'Paused',
        pausedAt ? ` at ${pausedAt}` : '',
        reason ? ` — ${reason}` : '',
      ].join(''),
  },
  findings: {
    narrowBanner: (n) =>
      `${n} blocking finding${n === 1 ? '' : 's'} must be resolved before approval.`,
    none: 'No policy findings.',
    suggestedFix: (fix) => `Suggested fix: ${fix}`,
    notWaivable: 'This is an absolute prohibition. It cannot be waived.',
    requestWaiver: 'Request waiver',
  },
};

const UiMessagesContext = createContext<UiMessages>(defaultUiMessages);

/** Supplies the app's localised messages to every shared component below it. */
export function UiMessagesProvider({
  messages,
  children,
}: {
  readonly messages: UiMessages;
  readonly children: ReactNode;
}) {
  return <UiMessagesContext value={messages}>{children}</UiMessagesContext>;
}

/** The messages in effect: the provider's, or the English defaults. */
export function useUiMessages(): UiMessages {
  return useContext(UiMessagesContext);
}

/**
 * Splits a translated sentence on `{name}` tokens and puts nodes in their place.
 *
 * Lets a catalogue own the whole sentence — word order included — while the
 * component still emphasises the account, brand or workspace inside it. Values
 * never pass through the template, so a brand called "{brand}" cannot inject.
 */
export function interpolateNodes(
  template: string,
  nodes: Readonly<Record<string, ReactNode>>,
): ReactNode {
  const parts = template.split(/(\{[A-Za-z0-9_]+\})/g);
  return (
    <>
      {parts.map((part, index) => {
        const name = /^\{([A-Za-z0-9_]+)\}$/.exec(part)?.[1];
        if (name !== undefined && name in nodes) {
          return <Fragment key={index}>{nodes[name]}</Fragment>;
        }
        return <Fragment key={index}>{part}</Fragment>;
      })}
    </>
  );
}
